/**
 * BacktestValidation — "Kiểm chứng tín hiệu"
 *
 * Displays forward-return analysis for Detector, MoneyFlow,
 * SmartMoney and Cross-engine Consensus signals.
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Alert, ToggleButtonGroup, ToggleButton, Skeleton,
} from '@mui/material';
import { Science, TrendingUp, TrendingDown, Biotech, GpsFixed, Extension, MilitaryTech, AccountBalanceWallet, Sensors, BarChart, Psychology, FlashOn, Equalizer, Link, Label, Settings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { backtestValidationAPI } from '../services/api';

// ── Group metadata ──────────────────────────────────────────
const GROUP_META = {
  DETECTOR:        { color: '#8b5cf6', Icon: Biotech },
  DET_QUALITY:     { color: '#8b5cf6', Icon: GpsFixed },
  DET_SETUP:       { color: '#8b5cf6', Icon: Extension },
  DET_RANK:        { color: '#8b5cf6', Icon: MilitaryTech },
  MF_LABEL:        { color: '#ef4444', Icon: AccountBalanceWallet },
  MF_SIGNAL:       { color: '#ef4444', Icon: Sensors },
  MF_SCORE:        { color: '#ef4444', Icon: BarChart },
  SM_LABEL:        { color: '#6366f1', Icon: Psychology },
  SM_SIGNAL:       { color: '#6366f1', Icon: FlashOn },
  SM_SCORE:        { color: '#6366f1', Icon: Equalizer },
  CONSENSUS_N:     { color: '#10b981', Icon: Link },
  CONSENSUS_LBL:   { color: '#10b981', Icon: Label },
  ENGINE_ACTIVE:   { color: '#f59e0b', Icon: Settings },
};

const HORIZONS = [1, 3, 5, 10, 20];

const fmtPct = (v) => v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '—';
const fmtHit = (v) => v != null ? `${v.toFixed(1)}%` : '—';
const retColor = (v) => v > 1 ? '#10b981' : v > 0 ? '#3b82f6' : v > -1 ? '#f59e0b' : '#ef4444';
const hitColor = (v) => v >= 55 ? '#10b981' : v >= 50 ? '#3b82f6' : v >= 45 ? '#f59e0b' : '#ef4444';

const CATEGORY_GROUPS = {
  detector: ['DETECTOR', 'DET_QUALITY', 'DET_SETUP', 'DET_RANK'],
  moneyflow: ['MF_LABEL', 'MF_SIGNAL', 'MF_SCORE'],
  smartmoney: ['SM_LABEL', 'SM_SIGNAL', 'SM_SCORE'],
  consensus: ['CONSENSUS_N', 'CONSENSUS_LBL', 'ENGINE_ACTIVE'],
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function BacktestValidation() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('detector');
  const [snack, setSnack] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await backtestValidationAPI.getResults();
      setData(resp.data);
      setSnack({ severity: 'success', msg: t('backtestValidation.snackSuccess', { count: resp.data.total }) });
    } catch (e) {
      setError(e.message);
      setSnack({ severity: 'error', msg: t('backtestValidation.snackError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => { loadData(); }, [loadData]);

  const visibleGroups = useMemo(() => {
    if (!data?.groups) return [];
    return (CATEGORY_GROUPS[category] || []).filter(g => data.groups[g]?.length > 0);
  }, [data, category]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Science sx={{ fontSize: 28, color: '#6366f1' }} />
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1e293b', lineHeight: 1.1 }}>
            {t('backtestValidation.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
            {t('backtestValidation.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Category toggle */}
      <ToggleButtonGroup value={category} exclusive onChange={(_, v) => v && setCategory(v)}
        size="small" sx={{ mb: 2, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.72rem', px: 1.5, py: 0.4 } }}>
        <ToggleButton value="detector"><Biotech sx={{ fontSize: 16, mr: 0.5 }} />{t('backtestValidation.categories.detector')}</ToggleButton>
        <ToggleButton value="moneyflow"><AccountBalanceWallet sx={{ fontSize: 16, mr: 0.5 }} />{t('backtestValidation.categories.moneyflow')}</ToggleButton>
        <ToggleButton value="smartmoney"><Psychology sx={{ fontSize: 16, mr: 0.5 }} />{t('backtestValidation.categories.smartmoney')}</ToggleButton>
        <ToggleButton value="consensus"><Link sx={{ fontSize: 16, mr: 0.5 }} />{t('backtestValidation.categories.consensus')}</ToggleButton>
      </ToggleButtonGroup>

      {/* Loading */}
      {loading && !data && (
        <Box>{[1,2,3].map(i => <Skeleton key={i} height={160} sx={{ mb: 1.5, borderRadius: 2 }} />)}</Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tables per group */}
      {visibleGroups.map(groupKey => {
        const meta = GROUP_META[groupKey] || { title: groupKey, color: '#64748b', Icon: Science };
        const rows = data.groups[groupKey] || [];
        const GroupIcon = meta.Icon;
        return (
          <Box key={groupKey} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
              <GroupIcon sx={{ fontSize: '1.1rem', color: meta.color }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: meta.color }}>{t(`backtestValidation.groups.${groupKey}`)}</Typography>
              <Chip label={`${rows.length}`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: alpha(meta.color, 0.08), color: meta.color }} />
            </Box>

            <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={hdr}>{t('backtestValidation.table.label')}</TableCell>
                    <TableCell sx={hdr} align="right">{t('backtestValidation.table.count')}</TableCell>
                    {HORIZONS.map(h => (
                      <React.Fragment key={h}>
                        <TableCell sx={hdr} align="right">{t('backtestValidation.table.avg', { d: h })}</TableCell>
                        <TableCell sx={hdr} align="right">{t('backtestValidation.table.hit', { d: h })}</TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell sx={hdr} align="right">{t('backtestValidation.table.dd', { d: 5 })}</TableCell>
                    <TableCell sx={hdr} align="right">{t('backtestValidation.table.dd', { d: 10 })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{r.label}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#64748b' }} align="right">{r.count}</TableCell>
                      {HORIZONS.map(h => {
                        const avg = r[`avg_${h}d`];
                        const hit = r[`hit_${h}d`];
                        return (
                          <React.Fragment key={h}>
                            <TableCell align="right" sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace', color: avg != null ? retColor(avg) : '#cbd5e1' }}>
                              {avg != null ? (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2 }}>
                                  {avg > 0 ? <TrendingUp sx={{ fontSize: 10 }} /> : <TrendingDown sx={{ fontSize: 10 }} />}
                                  {fmtPct(avg)}
                                </Box>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 600, color: hit != null ? hitColor(hit) : '#cbd5e1' }}>
                              {fmtHit(hit)}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                      <TableCell align="right" sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#ef4444', fontWeight: 600 }}>
                        {r.max_dd_5d != null ? `${r.max_dd_5d.toFixed(2)}%` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#ef4444', fontWeight: 600 }}>
                        {r.max_dd_10d != null ? `${r.max_dd_10d.toFixed(2)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {data && visibleGroups.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <Science sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography sx={{ fontWeight: 600 }}>{t('backtestValidation.noData')}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{t('backtestValidation.noDataHint')}: <code>.venv/bin/python3 scripts/backtest_smart_money_validation.py --backfill-sm</code></Typography>
        </Box>
      )}

      {loading && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', mt: 2 }} />}

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack && <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ fontSize: '0.78rem' }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}

const hdr = { fontWeight: 700, fontSize: '0.65rem', color: '#64748b', whiteSpace: 'nowrap' };
